import React, { useState, useEffect, useRef } from 'react';
import { Layout, Typography, notification } from 'antd';
import io from 'socket.io-client';
import Peer from 'peerjs';
import ReactGA from 'react-ga4';
import UserList from './components/user/UserList';
import FileTransfer from './components/file/FileTransfer';
import FileReceiver from './components/file/FileReceiver';
import Header from './components/common/Header';
import ConnectionTest from './components/common/ConnectionTest';
import InfoInstruction from './components/info/InfoInstruction';
import { generateUsernameFromPeerId } from './utils/usernameGenerator';
import 'antd/dist/reset.css';
import './index.css';
import CurrentUser from './components/user/CurrentUser';

const { Content } = Layout;
const { Title } = Typography;

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001';

// Initialize Google Analytics
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
if (GA_MEASUREMENT_ID) {
  ReactGA.initialize(GA_MEASUREMENT_ID);
}

function App() {
  const [socket, setSocket] = useState(null);
  const [peer, setPeer] = useState(null);
  const [peerId, setPeerId] = useState('');
  const [username, setUsername] = useState('');
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [incomingFileOffer, setIncomingFileOffer] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [peerConnected, setPeerConnected] = useState(false);
  const [fileTransfers, setFileTransfers] = useState(new Map());
  
  // Use refs to prevent multiple initializations
  const socketRef = useRef(null);
  const peerRef = useRef(null);
  const isInitialized = useRef(false);
  const fileTransfersRef = useRef(new Map());

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    // Track page view
    if (GA_MEASUREMENT_ID) {
      ReactGA.send({ hitType: "pageview", page: "/" });
    }

    console.log('Initializing connections...');
    console.log('Socket URL:', SOCKET_URL);

    let storedPeerId = localStorage.getItem('dropfilez-peer-id');
    
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 3,
      timeout: 10000,
      withCredentials: false,
      forceNew: true
    });
    
    socketRef.current = newSocket;
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('Connected to server:', newSocket.id);
      setSocketConnected(true);
      
      // Register user when socket connects and peer is ready
      if (peerId && username) {
        newSocket.emit('register-user', { peerId, username });
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      console.error('Error details:', error.message, error.type);
      setSocketConnected(false);
      
      // Show user-friendly error
      notification.error({
        message: 'Connection Error',
        description: 'Unable to connect to server. Please check if the server is running.',
        duration: 5
      });
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setSocketConnected(false);
    });

    // Move the users-updated listener to use current state values
    newSocket.on('users-updated', (users) => {
      console.log('Users updated:', users);
      console.log('Current peerId:', peerId); // Add this line
      setPeerId(currentPeerId => {
        console.log('Current peerId for filtering:', currentPeerId);
        console.log('All users received:', users); // Add this line
        const filteredUsers = users.filter(user => {
          const hasValidPeerId = user.peerId && user.peerId !== currentPeerId;
          console.log('User:', user, 'hasValidPeerId:', hasValidPeerId, 'user.peerId:', user.peerId, 'currentPeerId:', currentPeerId);
          return hasValidPeerId;
        });
        console.log('Filtered users:', filteredUsers);
        setConnectedUsers(filteredUsers);
        return currentPeerId;
      });
    });

    const newPeer = new Peer(storedPeerId || undefined, {
      debug: 1,
      config: {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' }
        ]
      }
    });
    
    peerRef.current = newPeer;
    setPeer(newPeer);

    newPeer.on('open', (id) => {
      console.log('Peer opened with ID:', id);
      setPeerId(id);
      setPeerConnected(true);
      localStorage.setItem('dropfilez-peer-id', id);
      
      // Generate a friendly username based on the peer ID
      const friendlyUsername = generateUsernameFromPeerId(id);
      setUsername(friendlyUsername);
      console.log('Generated username:', friendlyUsername);
      
      if (newSocket.connected) {
        console.log('Joining room with peer ID:', id, 'and username:', friendlyUsername);
        newSocket.emit('join', { 
          peerId: id, 
          username: friendlyUsername
        });
      } else {
        newSocket.on('connect', () => {
          console.log('Socket connected, now joining with peer ID:', id, 'and username:', friendlyUsername);
          newSocket.emit('join', { 
            peerId: id, 
            username: friendlyUsername
          });
        });
      }
    });

    newPeer.on('error', (err) => {
      console.error('Peer error:', err);
      setPeerConnected(false);
      if (err.type === 'unavailable-id') {
        localStorage.removeItem('dropfilez-peer-id');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    });

    newPeer.on('connection', (conn) => {
      console.log('Incoming connection from:', conn.peer);
      
      conn.on('data', (data) => {
        handleIncomingData(data, conn);
      });

      conn.on('error', (err) => {
        console.error('Connection error:', err);
      });
    });

    return () => {
      console.log('Cleaning up connections...');
      // Don't reset isInitialized in cleanup to prevent re-initialization in strict mode
      // isInitialized.current = false; // Remove this line
      
      // Only cleanup if we're actually unmounting (not just strict mode)
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.disconnect();
      }
      if (peerRef.current && !peerRef.current.destroyed) {
        peerRef.current.destroy();
      }
    };
  }, []); // Remove peerId from dependency array

  const handleIncomingData = (data, conn) => {
    console.log('Received data type:', data.type, 'from:', conn.peer);
    
    switch (data.type) {
      case 'file-offer':
        setIncomingFileOffer({
          from: conn.peer,
          fileInfo: data.fileInfo,
          connection: conn
        });
        break;
        
      case 'file-accepted':
        console.log('File accepted by:', conn.peer);
        sendFileData(conn, data.fileInfo);
        break;
        
      case 'file-rejected':
        console.log('File rejected by:', conn.peer);
        notification.info({
          message: 'File Rejected',
          description: 'The user rejected your file transfer'
        });
        break;
        
      case 'file-chunk':
        console.log('Received chunk for:', data.fileName, 'offset:', data.offset);
        receiveFileChunk(data, conn);
        break;
        
      case 'file-complete':
        console.log('File transfer complete for:', data.fileName);
        completeFileReceive(data);
        break;
        
      default:
        console.log('Unknown data type:', data.type);
    }
  };

  const sendFileOffer = (targetPeerId, file) => {
    if (!peerRef.current || !peerConnected) {
      notification.error({
        message: 'Connection Error',
        description: 'Peer connection not ready'
      });
      return;
    }
    
    // Track file send event
    if (GA_MEASUREMENT_ID) {
      ReactGA.event({
        category: 'File Transfer',
        action: 'Send Offer',
        label: file.type,
        value: Math.round(file.size / 1024) // File size in KB
      });
    }

    console.log('Sending file offer to:', targetPeerId);
    const conn = peerRef.current.connect(targetPeerId);
    
    conn.on('open', () => {
      console.log('Connection opened, sending file offer');
      
      // Store the file for sending later
      setFileTransfers(prev => new Map(prev.set(targetPeerId, {
        file,
        connection: conn,
        type: 'outgoing'
      })));
      
      conn.send({ 
        type: 'file-offer', 
        fileInfo: {
          name: file.name,
          size: file.size,
          type: file.type
        }
      });
      
      notification.success({
        message: 'File offer sent',
        description: `Waiting for ${targetPeerId.slice(0, 8)} to accept the file`
      });
    });

    conn.on('data', (data) => {
      handleIncomingData(data, conn);
    });

    conn.on('error', (err) => {
      console.error('Send file error:', err);
      notification.error({
        message: 'Failed to send file offer',
        description: 'Could not connect to the target user'
      });
    });
  };

  const sendFileData = (conn, fileInfo) => {
    const transfer = Array.from(fileTransfers.values()).find(t => t.connection === conn);
    if (!transfer || !transfer.file) {
      console.error('No file found for transfer');
      return;
    }

    const file = transfer.file;
    const chunkSize = 16384; // 16KB chunks
    const reader = new FileReader();
    let offset = 0;

    console.log(`Starting file transfer: ${file.name}, size: ${file.size}`);

    const sendNextChunk = () => {
      if (offset >= file.size) {
        // File transfer complete
        console.log('File transfer complete, sending completion signal');
        conn.send({
          type: 'file-complete',
          fileName: file.name,
          from: peerId
        });
        notification.success({
          message: 'File Sent',
          description: `Successfully sent ${file.name}`
        });
        
        // Clean up the transfer
        setFileTransfers(prev => {
          const newTransfers = new Map(prev);
          for (const [key, value] of newTransfers.entries()) {
            if (value.connection === conn) {
              newTransfers.delete(key);
              break;
            }
          }
          return newTransfers;
        });
        return;
      }

      const chunk = file.slice(offset, offset + chunkSize);
      reader.readAsArrayBuffer(chunk);
    };

    reader.onload = (e) => {
      console.log(`Sending chunk at offset ${offset}, size: ${e.target.result.byteLength}`);
      conn.send({
        type: 'file-chunk',
        data: e.target.result,
        offset: offset,
        fileName: file.name,
        totalSize: file.size,
        from: peerId
      });
      offset += chunkSize;
      
      // Send next chunk after a small delay
      setTimeout(sendNextChunk, 10);
    };

    reader.onerror = (err) => {
      console.error('File reading error:', err);
      notification.error({
        message: 'File Transfer Error',
        description: 'Error reading file data'
      });
    };

    sendNextChunk();
  };

  const receiveFileChunk = (data, conn) => {
    const transferId = `${conn.peer}-${data.fileName}`;
    console.log('Receiving chunk for transferId:', transferId, 'offset:', data.offset, 'size:', data.data.byteLength);

    // Update the ref synchronously so completeFileReceive always sees the latest chunks,
    // regardless of when React flushes the queued setState callback.
    const currentMap = fileTransfersRef.current;
    let existing = currentMap.get(transferId);

    if (!existing) {
      existing = {
        chunks: [],
        fileName: data.fileName,
        totalSize: data.totalSize,
        receivedSize: 0,
        type: 'incoming',
        connection: conn,
        from: conn.peer
      };
    }

    existing.chunks.push({
      data: data.data,
      offset: data.offset
    });
    existing.receivedSize += data.data.byteLength;

    const newTransfers = new Map(currentMap);
    newTransfers.set(transferId, existing);
    fileTransfersRef.current = newTransfers; // sync update — visible immediately

    console.log(`Transfer ${transferId}: ${existing.chunks.length} chunks, ${existing.receivedSize}/${existing.totalSize} bytes`);

    // Also update React state for UI re-render
    setFileTransfers(newTransfers);
  };

  const completeFileReceive = (data) => {
    console.log('Completing file receive for:', data.fileName);
    console.log('Available transfers in ref:', Array.from(fileTransfersRef.current.entries()));
    
    // Find the transfer by fileName using the ref
    let transfer = null;
    let foundKey = null;
    
    // Search through all transfers to find the one with matching fileName
    for (const [key, value] of fileTransfersRef.current.entries()) {
      if (value.fileName === data.fileName) {
        transfer = value;
        foundKey = key;
        console.log('Found transfer:', key, 'with', value.chunks ? value.chunks.length : 0, 'chunks');
        break;
      }
    }
    
    if (!transfer) {
      console.error('Transfer not found for file:', data.fileName);
      console.log('Available transfers:', Array.from(fileTransfersRef.current.entries()));
      notification.error({
        message: 'File Transfer Error',
        description: 'Transfer record not found'
      });
      return;
    }
    
    if (!transfer.chunks || transfer.chunks.length === 0) {
      console.error('No chunks found in transfer:', transfer);
      notification.error({
        message: 'File Transfer Error',
        description: 'No file chunks received'
      });
      return;
    }

    console.log(`Assembling file: ${data.fileName}, chunks: ${transfer.chunks.length}`);
    
    // Sort chunks by offset
    transfer.chunks.sort((a, b) => a.offset - b.offset);
    
    // Combine all chunks
    const totalSize = transfer.chunks.reduce((sum, chunk) => sum + chunk.data.byteLength, 0);
    console.log('Total assembled size:', totalSize);
    
    const combinedArray = new Uint8Array(totalSize);
    let currentOffset = 0;
    
    transfer.chunks.forEach((chunk, index) => {
      const chunkArray = new Uint8Array(chunk.data);
      combinedArray.set(chunkArray, currentOffset);
      currentOffset += chunk.data.byteLength;
      console.log(`Chunk ${index}: offset=${chunk.offset}, size=${chunk.data.byteLength}`);
    });

    // Create blob and download
    const blob = new Blob([combinedArray]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = data.fileName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log('File download triggered for:', data.fileName);
    
    notification.success({
      message: 'File Received',
      description: `Successfully received ${data.fileName}`
    });

    // Clean up both state and ref
    setFileTransfers(prev => {
      const newTransfers = new Map(prev);
      if (foundKey) {
        newTransfers.delete(foundKey);
      }
      fileTransfersRef.current = newTransfers;
      return newTransfers;
    });
  };

  const acceptFileOffer = () => {
    if (incomingFileOffer) {
      // Track file accept event
      if (GA_MEASUREMENT_ID) {
        ReactGA.event({
          category: 'File Transfer',
          action: 'Accept File',
          label: incomingFileOffer.fileInfo.type,
          value: Math.round(incomingFileOffer.fileInfo.size / 1024)
        });
      }

      console.log('Accepting file offer from:', incomingFileOffer.from);
      console.log('File info:', incomingFileOffer.fileInfo);
      
      // Store the incoming transfer info with proper structure
      const transferId = `${incomingFileOffer.from}-${incomingFileOffer.fileInfo.name}`;
      
      setFileTransfers(prev => {
        const newTransfers = new Map(prev);
        const transferData = {
          fileName: incomingFileOffer.fileInfo.name,
          fileInfo: incomingFileOffer.fileInfo,
          totalSize: incomingFileOffer.fileInfo.size,
          connection: incomingFileOffer.connection,
          type: 'incoming',
          chunks: [],
          receivedSize: 0,
          from: incomingFileOffer.from
        };
        
        newTransfers.set(transferId, transferData);
        console.log('Created transfer record:', transferId, transferData);
        
        // Also update the ref
        fileTransfersRef.current = newTransfers;
        
        return newTransfers;
      });
      
      incomingFileOffer.connection.send({ 
        type: 'file-accepted',
        fileInfo: incomingFileOffer.fileInfo
      });
      
      setIncomingFileOffer(null);
      
      notification.info({
        message: 'File Transfer Started',
        description: `Receiving ${incomingFileOffer.fileInfo.name}...`
      });
    }
  };

  const rejectFileOffer = () => {
    if (incomingFileOffer) {
      // Track file reject event
      if (GA_MEASUREMENT_ID) {
        ReactGA.event({
          category: 'File Transfer',
          action: 'Reject File',
          label: incomingFileOffer.fileInfo.type
        });
      }

      console.log('Rejecting file offer from:', incomingFileOffer.from);
      incomingFileOffer.connection.send({ 
        type: 'file-rejected' 
      });
      setIncomingFileOffer(null);
      
      notification.info({
        message: 'File Transfer Rejected',
        description: `You have rejected the file transfer from ${incomingFileOffer.from}`
      });
    }
  };

  return (
    <Layout className="app-layout">
      <Header peerId={peerId} peerConnected={peerConnected} username={username} />
      <Content style={{ padding: '24px' }}>
        <div className="app-container">
          <Title level={2}>DropFilez - Peer to Peer File Sharing on Local or WiFi Network</Title>
          
          <CurrentUser 
                peerId={peerId} 
                peerConnected={peerConnected} 
                username={username} 
              />

          <div className="main-content">
            <div className="users-section">
              <UserList 
                users={connectedUsers} 
                onSendFile={sendFileOffer}
              />
            </div>
            
            <div className="transfer-section">
              <FileTransfer 
                peer={peer}
                transfers={Array.from(fileTransfers.values())}
              />
            </div>
          </div>

          <InfoInstruction />
          <ConnectionTest />

          {incomingFileOffer && (
            <FileReceiver
              fileOffer={incomingFileOffer}
              onAccept={acceptFileOffer}
              onReject={rejectFileOffer}
            />
          )}
        </div>
      </Content>
    </Layout>
  );
}

export default App;
